import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../../lib/utils'

const checkboxVariants = cva(
  'h-4 w-4 shrink-0 disabled:cursor-not-allowed disabled:opacity-50 accent-pink-600 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring',
  {
    variants: {
      size: {
        md: 'h-4 w-4',
        lg: 'h-5 w-5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const labelTextVariants = cva('ml-2 select-none', {
  variants: {
    size: {
      md: 'text-base leading-6',
      lg: 'text-lg leading-[30px]',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

interface CheckboxComponentProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'size'>,
    VariantProps<typeof checkboxVariants> {
  onCheckedChange?: (checked: boolean) => void
  textClassName?: string
  labelClassName?: string
}

const Checkbox = React.forwardRef<
  React.ElementRef<'input'>,
  CheckboxComponentProps
>(
  (
    {
      children,
      className,
      onChange,
      onCheckedChange,
      textClassName,
      labelClassName,
      size,
      ...props
    },
    ref
  ) => (
    <>
      <label
        className={cn(
          'inline-flex cursor-pointer items-center',
          labelClassName
        )}
      >
        <input
          type="checkbox"
          ref={ref}
          {...props}
          onChange={React.useMemo(() => {
            if (onCheckedChange) {
              return (event: React.ChangeEvent<HTMLInputElement>) => {
                const { checked } = event.currentTarget
                onCheckedChange(checked)
                onChange?.(event)
              }
            }
            return onChange
          }, [onCheckedChange, onChange])}
          className={cn(checkboxVariants({ size, className }))}
        />
        <div
          className={cn(labelTextVariants({ size, className: textClassName }))}
        >
          {children}
        </div>
      </label>
    </>
  )
)

export { Checkbox }
